import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { axios } from "../../utils/axios";
import { toastOptions } from "../../config/toast";
import { getCurrentDate } from "../handlers";

export const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png", "image/webp", "webp"];

export const getActas = async (setIsLoading, setActas) => {
  setIsLoading(true);
  const result = await axios().get("get_actas");
  const { data, error } = result.data;
  if (data) {
    setActas(data);
  }
  if (error) {
    toast.error(error, toastOptions);
  }
  setIsLoading(false);
};

export const getFullActa = async (setIsLoading, setActa, setRecibos, selectedActaId, setImages, setImagenActa) => {
  setIsLoading(true);
  const result = await axios().get(`get_acta/${selectedActaId}`);
  const { data, error } = result.data;
  if (data) {
    setActa(data);
    setImages(data.imagenesBase64);
    setImagenActa(data.imagenActa);
    const recibos = data.recibos_acta.map((recibo) => ({
      historial_recibo: { ...recibo },
      isLoading: false,
      data: null,
    }));
    recibos.length > 0 ? setRecibos(recibos) : setRecibos(null);
  }
  if (error) {
    toast.error(error, toastOptions);
  }
  setIsLoading(false);
};

export const obtenerRecibo = async (numero_recibo, recibos, setRecibos, setExpanded, i) => {
  if (!recibos[i].data) {
    setRecibos((prevState) => {
      const newState = [...prevState];
      newState[i] = {
        ...prevState[i],
        isLoading: true,
      };
      return newState;
    });
    const result = await axios().post(`/obtener_recibo_backoffice`, {numero_recibo});
    if (!result.data.error && result.data.data) {
      setRecibos((prevState) => {
        const newState = [...prevState];
        newState[i] = {
          ...prevState[i],
          data: result.data.data,
          isLoading: false,
        };
        return newState;
      });
    } else {
      setExpanded(0)
      toast.error(result.data.error, toastOptions)
    }
  }
};

export const getInfoTitular = async (dominio, setInfoTitular, setIsLoadingInfoTitular) => {
  setIsLoadingInfoTitular(true);
  setInfoTitular(null);
  const result = await axios().post("/get_info_titular_acta", { dominio });
  const { data, error } = result.data;
  if (data && typeof data.value === "object" && !error) {
    setInfoTitular(data);
  }

  if (data && typeof data.value === "string") {
    setInfoTitular("");
  }

  if (error) {
    toast.error(error, toastOptions);
  }
  setIsLoadingInfoTitular(false);
};

export const getNombreInspector = async (id, setInfoInspector, setIsLoadingInfoInspector) => {
  setIsLoadingInfoInspector(true);
  setInfoInspector(null);
  const result = await axios().post("/get_nombre_inspector", { id });
  const { data, error } = result.data;

  if (data) {
    setInfoInspector(data);
  }

  if (error) {
    toast.error(error, toastOptions);
  }
  setIsLoadingInfoInspector(false);
};

export const getPersonInfo = async (submittedData, setIsLoading, setDni, setPersonValues) => {
  setIsLoading(true);
  setPersonValues(null);
  const result = await axios().post(`get_info_multado`, submittedData);
  const { data, error } = result.data;

  setIsLoading(false);
  if (error) {
    toast.error(error, toastOptions);
    setPersonValues(null);
    return;
  }
  setDni(submittedData.dni);
  setPersonValues(data);
  return;
};

export const darDeBajaSchema = yup.object({
  observacion: yup
    .string()
    .required("La observación es requerida")
    .test("len", "Maximo 255 caracteres", (val) => val.toString().length <= 255),
});

export const marcarActaEnSinaiSchema = yup.object({
  numero_acta_sinai: yup.string().required("El número de acta utilizado en SINAI es requerido"),
  observacion_carga_en_sinai: yup.string().required("La observación es requerida").max(255, "Máximo 255 caracteres"),
});

export const marcarActaComoPagadaSchema = yup.object({
  fecha_pago: yup
    .string()
    .matches(/^\d{4}-\d{2}-\d{2}$/)
    .required("La fecha de pago del acta es requerida")
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
  numero_recibo: yup
    .number()
    .required("El número de recibo es requerido")
    .transform((value, originalValue) => {
      return originalValue === "" ? null : value;
    }),
});

export const getPersonSchema = yup.object({
  dni: yup
    .number()
    .typeError("El DNI debe ser un numero")
    .required("Documento requerido")
    .test("len", "Maximo 8 caracteres", (val) => val.toString().length <= 8),
  gender: yup
    .string()
    .matches(/^[FMX, fmx]$/, {
      message: "Formato incorrecto. Debe ser M (masculino), F (femenino), o X",
      excludeEmptyString: true,
    })
    .required("Género requerido"),
});

export const generarCedulaSchema = yup.object({
  nombre_completo: yup.string().required("Nombre completo requerido"),
  numero_de_documento: yup
    .string()
    .required("El número de documento es requerido")
    .min(6, "Mínimo 6 dígitos")
    .max(8, "Máximo 8 dígitos"),
  domicilio_completo: yup.string().required("Domicilio completo requerido"),
});

export const actaSchema = yup.object({
  patente: yup
    .string()
    .required("La patente es requerida")
    .matches(
      /^(?:[A-Z,a-z]{3}\d{3}|[A-Z,a-z]{2}\d{3}[A-Z,a-z]{2}|\d{3}[A-Z, a-z]{3}|[A-Z, a-z]{1}\d{3}[A-Z,a-z]{3})$/,
      { message: "Formato incorrecto", excludeEmptyString: true }
    ),
  latitud: yup.number().required("Latitud requerida"),
  longitud: yup.number().required("Longitud requerida"),
  documento: yup
    .string()
    .test("isNumber", "El número de documento del conductor debe tenér como máximo 8 números", (value) => {
      return value === null || value === "" || value === undefined || /^[0-9]{1,8}$/.test(value);
    }),
  dependencia: yup
    .string()
    .matches(/^(DTM|DTP)$/, { message: "La dependencia debe ser DTM o DTP" })
    .required("La dependencia es requerida"),
  numero_acta: yup
    .string()
    .required("Número de acta requerido")
    .matches(/^[1-9]\d{0,7}$/, "El número de acta debe tenér como máximo 8 números y no debe comenzar con cero"),
  file_acta: yup.mixed().test("image", "Formatos validos: .webp, .jpg, .jpeg, o .png. ", (value) => {
    // Value es filelist. Si no hay archivo (value.length === 0) retorno true porque si no salta la validacion de formato, y file_acta no es un input requerido.
    if (value.length === 0) {
      return true;
    }
    return SUPPORTED_FORMATS.includes(value[0]?.type);
  }),
});

export const handleDarActaDeBaja = async (submittedData, setActa, setIsLoading) => {
  setIsLoading(true);
  const result = await axios().post("cancelar_acta", submittedData);
  const { data, error } = result.data;
  if (data) {
    setActa((prevState) => {
      return {
        ...prevState,
        observacion: data.observacion,
      };
    });
  }
  if (error) {
    toast.error(error, toastOptions);
  }
  setIsLoading(false);
  return;
};

export const handleActaSubmit = async (
  submittedData,
  articulosSeleccionados,
  setActas,
  setCargarActaModalIsOpen,
  setIsLoading
) => {
  let formData = {
    ...submittedData,
    file_acta: submittedData.file_acta[0],
    patente: submittedData.patente?.toUpperCase(),
  };
  formData.articulos = [];
  articulosSeleccionados?.map((articulo, i) => (formData.articulos[i] = articulo.data));
  setIsLoading(true);
  const result = await axios().post(`save_acta`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  const { status } = result;
  const { data, error } = result.data;
  if (data) {
    const result = await axios().get(`get_datagrid_acta/${data.acta.id}`);
    const { data: datagridActa } = result.data;
    /* Creo objeto acta con info necesaria para poder usar en la tabla */
    if (datagridActa) {
      setActas((prevState) => {
        return [datagridActa[0], ...prevState];
      });

      setCargarActaModalIsOpen(false);
      toast.success("Acta cargada correctamente");
    }
  }

  if (error) {
    if (status !== 422) {
      toast.error(error, toastOptions);
    } else {
      Object.values(error).map((err) => toast.error(err[0], toastOptions));
    }
  }
  setIsLoading(false);
};

export const multadoSchema = yup.object({
  numero_de_documento: yup
    .number()
    .typeError("El DNI debe ser un numero")
    .required("Documento requerido")
    .test("len", "Maximo 8 caracteres", (val) => val.toString().length <= 8),
  nombre: yup.string().max(255, "Máximo 255 caractéres").required("Nombre/s requerido"),
  apellido: yup.string().max(255, "Máximo 255 caractéres").required("Apellido/s requerido"),
  direccion: yup.string().max(255, "Máximo 255 caractéres").required("Direccion requerida"),
  localidad: yup.string().max(255, "Máximo 255 caractéres").required("Localidad requerida"),
  provincia: yup.string().max(255, "Máximo 255 caractéres").required("Provincia requerida"),
});

export const onSubmitSavePersonaMultada = async (submittedData, setIsLoading, setModalLlenarInfoIsOpen, setActa) => {
  setIsLoading(true);
  const result = await axios().post("save_persona_multada", submittedData);
  const { status } = result;
  const { data, error } = result.data;

  if (data) {
    setActa((prevState) => ({
      ...prevState,
      id_persona_multado: result.data.data.id,
      persona_multada: result.data.data,
    }));
    toast.success("Información editada correctamente", toastOptions);
    setModalLlenarInfoIsOpen(false);
  }

  if (error) {
    if (status !== 422) {
      toast.error(error, toastOptions);
    } else {
      Object.values(error).map((err) => toast.error(err[0], toastOptions));
    }
  }

  setIsLoading(false);
};

export const handleAprobarActa = async (setIsLoading, setActa, actaId) => {
  setIsLoading(true);
  const result = await axios().post("aprobar_acta", { actaId: actaId });
  const { data, error } = result.data;
  if (data) {
    toast.success("Acta aprobada correctamente", toastOptions);
    setActa((prevState) => ({
      ...prevState,
      verificada: data,
    }));
  }
  if (error) {
    toast.error(error, toastOptions);
  }
  setIsLoading(false);
};

export const tableData = (data) => {
  const columns = [
    { field: "id", headerName: "ID", hide: true },
    {
      field: "created_at",
      headerName: "Fecha",
      minWidth: 175,
      flex: 1.3,
      renderCell: (params) => {
        const date = new Date(params.row.created_at);
        return <p className='mb-0'>{date.toLocaleString()}</p>;
      },
    },
    {
      field: "numero_acta",
      headerName: "# Acta",
      minWidth: 125,
      flex: 1,
    },
    {
      field: "patente",
      headerName: "Patente",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "numero_recibo",
      headerName: "# Recibo",
      minWidth: 125,
      flex: 1,
    },
    {
      field: "estado",
      headerName: "Estado Acta",
      minWidth: 250,
      flex: 1,
      renderCell: (params) => {
        const fecha_pago = params.row.ultimo_recibo_generado?.fecha_pago;
        const vencio = params.row.ultimo_recibo_generado?.vencio;
        let alert = "alert ";
        let textContent = "";

        if (params.row.observacion) {
          alert += "alert-danger";
          textContent = "Dado de baja";
        } else if (params.row.fecha_cargada_en_sinai) {
          alert += "alert-info";
          textContent = "Cargada en SINAI";
        } else if (params.row.ultimo_recibo_generado) {
          if (fecha_pago) {
            const dateFechaPago = new Date(fecha_pago);
            alert += "alert-success";
            textContent = "Pagado - " + dateFechaPago.toLocaleDateString("es-AR", { timeZone: "UTC" });
          } else if (!fecha_pago && !vencio) {
            alert += "alert-warning";
            textContent = "No Pagado";
          } else if (!fecha_pago && vencio) {
            alert += "alert-danger";
            textContent = "No Pagado - Vencido";
          }
        } else {
          alert += "alert-danger";
          textContent = "Sin Recibo";
        }
        return (
          <Box className={"w-100 h-100 m-0 border-0 rounded-0 " + alert}>
            <p className='w-100 h-100 d-flex justify-content-center align-items-center fw-bold'>{textContent}</p>
          </Box>
        );
      },
    },
    {
      field: "dni_multado",
      headerName: "DNI Multado",
      minWidth: 200,
      flex: 1,
      renderCell: (params) => {
        return <p className='mb-0'> {params.row.dni_multado ?? "No se encontraba presente"}</p>;
      },
    },
    {
      field: "dni_titular",
      headerName: "Identificacion Titular",
      minWidth: 200,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Acciones",
      minWidth: 200,
      flex: 2,
      renderCell: (params) => {
        return (
          <>
            <Box>
              <Link to={`/actas/${params.row.id}`} style={{ textDecoration: "none" }}>
                <VisibilityIcon /> Ver
              </Link>
            </Box>
          </>
        );
      },
    },
  ];

  const rows = data.map((d, index) => {
    return {
      id: d.id,
      user_id_inspector: d.user_id_inspector,
      dni_multado: d.dni_multado,
      dni_titular: d.dni_titular,
      ultimo_recibo_generado: d.ultimo_recibo_generado,
      numero_recibo: d.ultimo_recibo_generado?.numero_recibo,
      fecha_cargada_en_sinai: d.fecha_cargada_en_sinai,
      estado: d.fecha_cargada_en_sinai
        ? "0"
        : d.observacion
        ? "1"
        : !d.ultimo_recibo_generado
        ? "2"
        : !d.ultimo_recibo_generado?.fecha_pago && d.ultimo_recibo_generado?.vencio
        ? "3"
        : !d.ultimo_recibo_generado?.fecha_pago && !d.ultimo_recibo_generado?.vencio
        ? "4"
        : d.ultimo_recibo_generado?.fecha_pago
        ? "5"
        : null,
      patente: d.patente,
      numero_acta: d.dependencia + "-" + d.numero_acta,
      dependencia: d.dependencia,
      fecha_pago: d.fecha_pago,
      created_at: d.created_at,
      observacion: d.observacion,
    };
  });
  const filter = (d, value) => {
    value = value.toLowerCase();
    return (
      String(d.dni_multado)?.toString().toLowerCase().includes(value) ||
      String(d.dni_titular)?.toLowerCase().includes(value) ||
      String(d.patente)?.toLowerCase().includes(value) ||
      String(d.numero_acta)?.toLowerCase().includes(value) ||
      String(d.numero_recibo)?.toLowerCase().includes(value)
    );
  };

  return { columns, rows, filter };
};

export const generarCedulaDeNotificacion = async (submittedData, acta, setActa, setIsLoadingCedula) => {
  try {
    const result = await axios().post(`generar_cedula_notificacion`, submittedData, {
      responseType: "blob",
    });

    if (result.data) {
      const blob = new Blob([result.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `Cédula de Notificación Acta ${acta.dependencia}-${acta.numero_acta}`;
      link.click();
      if (!acta.fecha_notificacion) {
        setActa((prevState) => ({
          ...prevState,
          fecha_notificacion: getCurrentDate(),
        }));
      }
    }
  } catch (error) {
    toast.error(error, toastOptions);
  }
};

export const actaVencida = (fechaDeCreacion) => {
  let dateFechaDeCreacion = new Date(fechaDeCreacion);
  let dateFechaActual = new Date();
  dateFechaDeCreacion.setHours(23, 59, 59);
  let diffInMilliseconds = dateFechaActual - dateFechaDeCreacion;
  let diffInDays = diffInMilliseconds / (1000 * 60 * 60 * 24);
  return diffInDays >= 60;
};

export const marcarActaCargadaEnSinai = async (submittedData, setActa, setIsLoading) => {
  try {
    setIsLoading(true);
    const result = await axios().post("marcar_acta_cargada_en_sinai", submittedData);
    const { status } = result;
    const { data, error } = result.data;
    setIsLoading(false);
    if (data) {
      setActa((prevState) => ({
        ...prevState,
        fecha_cargada_en_sinai: data,
        numero_acta_sinai: submittedData.numero_acta_sinai,
        observacion_carga_en_sinai: submittedData.observacion_carga_en_sinai,
      }));
      toast.success("Acta marcada correctamente", toastOptions);
    }

    if (error) {
      if (status !== 422) {
        toast.error(error, { ...toastOptions, autoClose: false });
      } else {
        Object.values(error).map((err) => toast.error(err[0], toastOptions));
      }
    }
  } catch (error) {
    toast.error(error, toastOptions);
  }
};

export const marcarActaComoPagada = async (submittedData, setActa, setRecibos, setError, setIsLoading) => {
  try {
    setIsLoading(true);
    let fechaDePago = new Date(submittedData.fecha_pago);
    let hoy = new Date();
    if (fechaDePago > hoy) {
      setError("fecha_pago", {
        type: "dateComparison",
        message: "La fecha de pago no puede ser mayor que la fecha actual",
      });
      return;
    }

    const result = await axios().post("marcar_acta_como_pagada", submittedData);
    const { status } = result;
    const { data, error } = result.data;

    if (data) {
      setActa(data);
      const recibos = data.recibos_acta.map((recibo) => ({
        historial_recibo: { ...recibo },
        isLoading: false,
        data: null,
      }));
      recibos.length > 0 ? setRecibos(recibos) : setRecibos(null);
      toast.success(`Se actualizó el acta correctamente (recibo ${submittedData.numero_recibo})`, toastOptions);
    }

    if (error) {
      if (status !== 422) {
        toast.error(error, toastOptions);
      } else {
        Object.values(error).map((err) => toast.error(err[0], toastOptions));
      }
    }
    setIsLoading(false);
  } catch (error) {
    toast.error(error, toastOptions);
  }
};
